"use client";

import { useEffect, useRef } from "react";
import {
  useNamespacedTranslations,
  useRegisterFilter,
  useSingleValueParameter,
  type FilterControllerProps,
  type FilterMenuItem,
  type ActiveChip,
  SingleSelectFilterMenu,
} from "@zapier/shared";

const VALID_STATUS = new Set<"on" | "off">(["on", "off"]);
const ICON = "statusStatus";

export function StatusFilterController({
  isOpen,
  namespace,
  onClose,
  onInteraction,
}: FilterControllerProps): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);
  const status = useSingleValueParameter<"on" | "off">("status", {
    validValues: VALID_STATUS,
  });

  // Deferred close: keep the menu open until the URL value reflects the pick,
  // then close — mirrors old ZapFilterBar closeOnNextValueReference (lines 234-246).
  const closeOnNextValue = useRef(false);
  useEffect(() => {
    if (closeOnNextValue.current && status.value) {
      closeOnNextValue.current = false;
      onClose();
    }
  }, [status.value, onClose]);

  // Chip labels ported from useFilterChipLabels getStatusChipLabel / getStatusChipAriaLabel.
  const label =
    status.value === "on"
      ? t("status.chipLabel.on")
      : status.value === "off"
        ? t("status.chipLabel.off")
        : "";
  const ariaLabel =
    status.value === "on"
      ? t("status.chipAriaLabel.on")
      : status.value === "off"
        ? t("status.chipAriaLabel.off")
        : "";

  const hasValue = !!status.value;
  let chip: ActiveChip | undefined;
  let signature = "status:none";
  if (hasValue) {
    chip = {
      key: "status",
      iconName: ICON,
      title: t("status.label"),
      label,
      ariaLabel,
    };
    signature = `status:${status.value}`;
  } else if (isOpen) {
    chip = {
      key: "status",
      iconName: ICON,
      title: t("status.label"),
      ariaLabel: t("status.label"),
      isDraft: true,
    };
    signature = "status:draft";
  }

  const items: FilterMenuItem[] = [
    { id: "on", name: t("status.on") },
    { id: "off", name: t("status.off") },
  ];

  useRegisterFilter({
    type: "status",
    hasValue,
    signature,
    chip,
    renderMenu: () => (
      <SingleSelectFilterMenu
        label={t("status.label")}
        items={items}
        selectedId={status.value}
        onSelect={(id) => {
          if (id !== "on" && id !== "off") {
            onClose();
            return;
          }
          // setParams no-ops when the URL is unchanged, so deferred close never
          // runs — close immediately when the already-active option is reselected.
          if (id === status.value) {
            onClose();
            return;
          }
          closeOnNextValue.current = true;
          status.set(id);
          onInteraction?.();
        }}
      />
    ),
    // React Aria submenu (Filters-button path): RA closes the tree on select.
    menuOptions: {
      label: t("status.label"),
      items,
      selectedId: status.value,
      onSelect: (id) => {
        if ((id === "on" || id === "off") && id !== status.value) {
          status.set(id);
          onInteraction?.();
        }
      },
    },
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
